import { useEffect, useRef } from 'react'
import crashSocket from '../services/crashSocket'
import useAppStore from '../../../store/useAppStore'
import useCrashStore from '../store/useCrashStore'
import { SERVER_EVENTS } from '../config/crashConfig'
import { isCrashUpdateBlocked } from '../../../services/appUpdateSafety'

/**
 * useCrashSocket — подключение к crash-сокету и делегирование всех событий в store.
 *
 * Подписывается при монтировании экрана Crash, отписывается при размонтировании.
 * На `connect` повторяет subscribe — сервер сам пришлёт снапшот состояния (§8).
 *
 * Подписки на события устанавливаются один раз (через ref на стабильные обработчики).
 * Обработчики берут методы из store напрямую через useCrashStore.getState() — это
 * гарантирует, что в callback'ах socket.io всегда актуальные функции без stale-замыканий.
 */
export const useCrashSocket = () => {
  // Ссылка на индикатор «переподключаемся» (показываем через ~3с после disconnect)
  const reconnectTimerRef = useRef(null)
  // Признак, что мы уже видели первый connect — чтобы не показывать плашку до него
  const wasConnectedRef = useRef(false)

  useEffect(() => {
    const store = useCrashStore.getState()
    // Свой chatId — до первого crash:bet, чтобы найти свою ставку в снапшоте crash:players
    // после перезагрузки/повторного входа на экран (§8.4)
    store.setMyChatId(crashSocket.resolveChatId())
    // Стартовая отрисовка инвентарём из REST, пока поднимается сокет
    if (!useAppStore.getState().isGuest) store.initFromRest()

    // Подключаем сокет и подписываемся на все события сервера

    // Обработчики — тонкие обёртки над store-методами
    const handleConnect = () => {
      useCrashStore.getState().setConnected(true)
      if (crashSocket.isAuthenticated()) useCrashStore.getState().setAuthorized(true)
      useCrashStore.getState().setShowReconnectNotice(false)
      wasConnectedRef.current = true
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const handleDisconnect = () => {
      useCrashStore.getState().setConnected(false)
      // Подтверждение вывода после разрыва не придёт — не держим кнопку заблокированной (§8.5)
      useCrashStore.getState().unlockCashout()
      // Показываем индикатор не сразу — обрыв 1-2с штатный (§8.1)
      if (wasConnectedRef.current && !reconnectTimerRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          useCrashStore.getState().setShowReconnectNotice(true)
        }, 3000)
      }
    }

    // События сервера → store
    const isCurrentAuth = () => crashSocket.authToken && crashSocket.resolveAuthToken() === crashSocket.authToken
    const handlers = {
      [SERVER_EVENTS.TIME]: (e) => useCrashStore.getState().onTime(e.serverTime),
      'server:time': (e) => useCrashStore.getState().onTime(e.serverTime),
      'user:balance': (e) => {
        if (isCurrentAuth()) useCrashStore.getState().onBalance(e)
      },
      'user:inventory': () => {
        if (isCurrentAuth()) useCrashStore.getState().initFromRest(true)
      },
      [SERVER_EVENTS.ROUND]: (e) => useCrashStore.getState().onRound(e),
      [SERVER_EVENTS.RUN]: (e) => useCrashStore.getState().onRun(e),
      [SERVER_EVENTS.TICK]: (e) => useCrashStore.getState().onTick(e),
      [SERVER_EVENTS.BUST]: (e) => useCrashStore.getState().onBust(e),
      [SERVER_EVENTS.BET]: (e) => { if (isCurrentAuth()) useCrashStore.getState().onMyBet(e) },
      [SERVER_EVENTS.BETS]: (e) => useCrashStore.getState().onBets(e),
      [SERVER_EVENTS.CASHOUT]: (e) => { if (isCurrentAuth()) useCrashStore.getState().onMyCashout(e) },
      [SERVER_EVENTS.CASHOUTS]: (e) => useCrashStore.getState().onCashouts(e),
      [SERVER_EVENTS.WIN_SKIN]: (e) => { if (isCurrentAuth()) useCrashStore.getState().onWinSkin(e) },
      [SERVER_EVENTS.INVENTORY]: (e) => { if (isCurrentAuth()) useCrashStore.getState().onInventory(e) },
      [SERVER_EVENTS.PLAYERS]: (e) => useCrashStore.getState().onPlayers(e),
      [SERVER_EVENTS.HISTORY]: (e) => useCrashStore.getState().onHistory(e),
      [SERVER_EVENTS.BALANCE]: (e) => {
        if (isCurrentAuth()) useCrashStore.getState().onBalance(e)
      },
      [SERVER_EVENTS.ALERT]: (e) => useCrashStore.getState().onAlert(e),
      [SERVER_EVENTS.DISABLED]: () => useCrashStore.getState().onDisabled()
    }

    // Регистрируем подписки
    Object.entries(handlers).forEach(([event, handler]) => {
      crashSocket.on(event, handler)
    })

    crashSocket.onConnect(handleConnect)
    crashSocket.onDisconnect(handleDisconnect)
    crashSocket.subscribe('crash')
    if (crashSocket.isConnected()) handleConnect()

    // При возврате во вкладку запрашиваем серверный снапшот ставок и раунда,
    // даже если соединение не разрывалось: браузер мог пропустить события в фоне.
    // Множитель по-прежнему считает RAF; тик не подделываем, иначе watchdog
    // ошибочно разрешит рост без подтверждения сервера (§8.1).
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      crashSocket.connect()
      if (crashSocket.isConnected()) crashSocket.requestState('crash')
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      // Отписка от событий
      Object.entries(handlers).forEach(([event, handler]) => {
        crashSocket.off(event, handler)
      })
      crashSocket.offConnect(handleConnect)
      crashSocket.offDisconnect(handleDisconnect)
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      // Отписываемся от Crash и сбрасываем состояние экрана
      crashSocket.unsubscribe('crash')
      const current = useCrashStore.getState()
      current.reset(isCrashUpdateBlocked(current))
    }
  }, [])
}

export default useCrashSocket
